import React from 'react';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { LinkButton } from '@ui/button';
import { CtaShell, IMAGE_SIZES, ResponsiveImage } from '@web/molecules';
import { Eyebrow } from '@web/atoms';
import { Typewriter } from '@web/atoms/Typewriter';
import { useShopifyOfferContext } from '../../offer/shopifyContext';
import { useReporter } from '../../reporter';
import { MuxVideo } from '../MuxVideo';
import { ctaButtonClicked } from './events';
import {
  Content,
  ButtonContainer,
  ImageContainer,
  VideoContainer,
  StyledSplitScreenPromo,
  BadgesWrapper,
  TypewriterStyledEyebrowContainer,
  TypewriterStyledEyebrow,
  MultiImageContainer,
  MultiImageGroup,
  BackImageWrapper,
  FrontImageWrapper,
} from './styles.js';

export const SplitScreenPromo = ({
  contentBgColor,
  imageBgColor,
  fontColor = 'text.primary',
  image,
  backImage,
  video,
  link,
  linkAlignment = 'center',
  imageFirst = false,
  showVideo = false,
  content,
  badges,
  eyebrow,
  typewriterEyebrow,
  showTypewriterAnimation = false,
}) => {
  const reporter = useReporter();
  const { formattedAmount } = useShopifyOfferContext();
  const showAnimation =
    showTypewriterAnimation && typewriterEyebrow?.words?.length > 1;

  return (
    <StyledSplitScreenPromo>
      <Content $fontColor={fontColor} $bgColor={contentBgColor}>
        {eyebrow ? (
          <Eyebrow textAlign="center">
            {interpolate(eyebrow, { offer: formattedAmount })}
          </Eyebrow>
        ) : showAnimation ? (
          <Typewriter {...typewriterEyebrow} />
        ) : typewriterEyebrow?.words?.length > 0 ? (
          <TypewriterStyledEyebrowContainer>
            <TypewriterStyledEyebrow
              $typography={typewriterEyebrow.typography}
              $fontColor={typewriterEyebrow.fontColor}
            >
              {interpolate(typewriterEyebrow?.words[0], {
                offer: formattedAmount,
              })}
            </TypewriterStyledEyebrow>
          </TypewriterStyledEyebrowContainer>
        ) : null}
        {content}
        {link && (
          <ButtonContainer $linkAlignment={linkAlignment}>
            <CtaShell
              defaultWidth="300px"
              fullWidth={link.fullWidth}
              fullWidthMobile={link.fullWidthMobile}
              fullWidthDesktop={link.fullWidthDesktop}
              sideMargin={link.sideMargin}
            >
              <LinkButton
                variant={link.variant}
                href={link.target}
                headerText={link.headerText}
                verticalPadding={6}
                fullWidth
                onClick={() =>
                  reporter.tag(
                    ctaButtonClicked(
                      link.text,
                      content?.[0]?.props?.content?.heading_text,
                    ),
                  )
                }
              >
                {link.text}
              </LinkButton>
            </CtaShell>
          </ButtonContainer>
        )}
        {Boolean(badges?.length) && (
          <BadgesWrapper $linkAlignment={linkAlignment}>
            {badges?.map((badge, index) => {
              return (
                <ResponsiveImage
                  {...badge}
                  width={badge?.width > 150 ? 150 : badge?.width}
                  sizes={IMAGE_SIZES.fixed(
                    badge?.width > 150 ? 150 : badge?.width,
                  )}
                  key={`${badge?.src}-${index}`}
                />
              );
            })}
          </BadgesWrapper>
        )}
      </Content>
      {showVideo && video ? (
        <VideoContainer $bgColor={imageBgColor} $imageFirst={imageFirst}>
          <MuxVideo
            playbackId={video.playbackId}
            autoplay={video.autoplay}
            loop={video.loop}
            thumbnail={video.thumbnail}
          />
        </VideoContainer>
      ) : backImage?.src && image?.src ? (
        <MultiImageContainer $imageFirst={imageFirst}>
          <MultiImageGroup>
            <BackImageWrapper $bgColor={imageBgColor} $imageFirst={imageFirst}>
              <ResponsiveImage
                {...backImage}
                sizes={IMAGE_SIZES.halfAtLaptop}
              />
            </BackImageWrapper>
            <FrontImageWrapper $imageFirst={imageFirst}>
              <ResponsiveImage {...image} sizes={IMAGE_SIZES.halfAtLaptop} />
            </FrontImageWrapper>
          </MultiImageGroup>
        </MultiImageContainer>
      ) : image?.src ? (
        <ImageContainer $bgColor={imageBgColor} $imageFirst={imageFirst}>
          <ResponsiveImage {...image} sizes={IMAGE_SIZES.halfAtLaptop} />
        </ImageContainer>
      ) : null}
    </StyledSplitScreenPromo>
  );
};

SplitScreenPromo.displayName = 'SplitScreenPromo';

SplitScreenPromo.propTypes = {
  contentBgColor: PropTypes.string,
  imageBgColor: PropTypes.string,
  fontColor: PropTypes.string,
  image: PropTypes.object,
  backImage: PropTypes.object,
  video: PropTypes.shape({
    playbackId: PropTypes.string,
    autoplay: PropTypes.bool,
    loop: PropTypes.bool,
    thumbnail: PropTypes.string,
  }),
  link: PropTypes.shape({
    target: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    variant: PropTypes.string,
    headerText: PropTypes.string,
    fullWidth: PropTypes.bool,
    fullWidthMobile: PropTypes.bool,
    fullWidthDesktop: PropTypes.bool,
  }),
  linkAlignment: PropTypes.string,
  imageFirst: PropTypes.bool,
  showVideo: PropTypes.bool,
  content: PropTypes.node,
  badges: PropTypes.arrayOf(PropTypes.object),
  eyebrow: PropTypes.string,
  typewriterEyebrow: PropTypes.shape({
    words: PropTypes.arrayOf(PropTypes.string),
    fontColor: PropTypes.string,
    typography: PropTypes.string,
  }),
  showTypewriterAnimation: PropTypes.bool,
};
